import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOpenParam } from '../hooks/useOpenParam';
import apiClient from '../api/client';
import { DataGrid, type Column } from '../components/ui/DataGrid';
import { useToast } from '../components/ui/Toast';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Select } from '../components/ui/Select';
import { Skeleton } from '../components/ui/Skeleton';
import { formatEUR, formatDate } from '../lib/format';
import { enumLabel } from '../lib/enumLabel';
import { usePermission } from '../auth/usePermission';
import { Permission } from '../auth/permissions';
import { Download, FileCheck, FilePlus, Plus, Trash2, X } from 'lucide-react';

interface CreditNote {
  id: string;
  credit_note_number: string;
  invoice_id: string | null;
  customer_id: string | null;
  subtotal_ht: string;
  vat_amount: string;
  total_ttc: string;
  reason: string;
  status: string;
  created_at: string;
}

interface Invoice {
  id: string;
  invoice_number: string;
}

/**
 * WP-4 §8c — an avoir the system PREPARED but deliberately did not issue: a supplier short-ship
 * closed a prepaid customer's line, so money was taken for goods that will never arrive. Valuing
 * it is automatic; issuing it is a fiscal act and stays with ACCOUNTING/OWNER.
 */
interface CreditNoteDraft {
  id: string;
  orderId: string;
  orderNumber: string | null;
  customerName: string | null;
  amountTTC: number;
  reason: string;
  status: 'PENDING' | 'ISSUED' | 'DISMISSED';
  createdAt: string;
}

/** Screens 35-36 — Credit Notes (avoirs). ACCOUNTING manually creates an AVO-YYYY-NNNNN
 *  credit note referencing an original invoice (V1; auto-from-returns is V2). */
export default function CreditNotes() {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const canCreate = usePermission(Permission.CREDIT_NOTE_CREATE);
  const [rows, setRows] = useState<CreditNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<CreditNote | null>(null);
  const [creating, setCreating] = useState(false);

  const reload = () => {
    setLoading(true);
    apiClient
      .get<CreditNote[]>('/credit-notes')
      .then((r) => setRows(r.data ?? []))
      .catch(showApiError)
      .finally(() => setLoading(false));
  };
  useEffect(reload, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Arriving from the Documents screen with `?open=<id>`: open that avoir's panel.
  useOpenParam(rows, loading, setSelected);

  const columns: Column<CreditNote>[] = [
    {
      key: 'credit_note_number',
      header: t('creditNotes.columns.number'),
      render: (r) => <span className="font-mono">{r.credit_note_number}</span>,
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (r) => enumLabel('docStatus', r.status),
    },
    { key: 'reason', header: t('creditNotes.columns.reason') },
    {
      key: 'total_ttc',
      header: t('creditNotes.columns.totalTtc'),
      render: (r) => formatEUR(r.total_ttc),
    },
    {
      key: 'created_at',
      header: t('creditNotes.columns.created'),
      render: (r) => formatDate(r.created_at),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('creditNotes.title')}
        description={t('creditNotes.description')}
        actions={
          canCreate ? (
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> {t('creditNotes.new')}
            </Button>
          ) : undefined
        }
      />
      <PendingDraftsPanel canDecide={canCreate} onIssued={reload} />
      <DataGrid
        columns={columns}
        rows={rows}
        loading={loading}
        rowKey={(r) => r.id}
        onRowClick={(r) => setSelected(r)}
      />
      {selected && (
        <CreditNoteDetailPanel creditNote={selected} onClose={() => setSelected(null)} />
      )}
      {creating && (
        <CreateCreditNoteModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            reload();
          }}
        />
      )}
    </div>
  );
}

/**
 * The queue of prepared avoirs waiting on a human. It renders only when something is waiting —
 * an empty permanent panel trains people to stop looking at it, and this is the one place where
 * a customer's refund sits until somebody acts.
 */
function PendingDraftsPanel({ canDecide, onIssued }: { canDecide: boolean; onIssued: () => void }) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [drafts, setDrafts] = useState<CreditNoteDraft[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const reload = () => {
    apiClient
      .get<CreditNoteDraft[]>('/credit-notes/drafts', { params: { status: 'PENDING' } })
      .then((r) => setDrafts(r.data ?? []))
      .catch(showApiError);
  };
  useEffect(reload, []); // eslint-disable-line react-hooks/exhaustive-deps

  const issue = async (d: CreditNoteDraft) => {
    setBusy(d.id);
    try {
      const r = await apiClient.post<{ creditNoteNumber: string; totalTTC: number }>(
        `/credit-notes/drafts/${d.id}/issue`
      );
      push({
        tone: 'success',
        message: t('creditNotes.drafts.issued', {
          number: r.data.creditNoteNumber,
          amount: formatEUR(r.data.totalTTC),
        }),
      });
      reload();
      onIssued();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (d: CreditNoteDraft) => {
    // A dismissal is a decision on somebody's money, so it is recorded with its reason.
    const note = window.prompt(
      t('creditNotes.drafts.dismissPrompt', { order: d.orderNumber ?? d.orderId })
    );
    if (!note || note.trim().length < 3) return;
    setBusy(d.id);
    try {
      await apiClient.post(`/credit-notes/drafts/${d.id}/dismiss`, { note: note.trim() });
      push({ tone: 'success', message: t('creditNotes.drafts.dismissed') });
      reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(null);
    }
  };

  if (drafts.length === 0) return null;

  return (
    <section className="border border-warning/40 rounded p-4 bg-warning/5 mb-4">
      <h2 className="font-semibold mb-1">
        {t('creditNotes.drafts.title', { count: drafts.length })}
      </h2>
      <p className="text-xs text-muted-foreground mb-3">{t('creditNotes.drafts.intro')}</p>
      <table className="w-full text-sm">
        <thead className="text-muted-foreground">
          <tr>
            <th className="text-left py-1">{t('creditNotes.drafts.order')}</th>
            <th className="text-left">{t('creditNotes.drafts.customer')}</th>
            <th className="text-right">{t('creditNotes.drafts.amountTtc')}</th>
            <th className="text-left">{t('creditNotes.drafts.preparedOn')}</th>
            {canDecide && <th className="text-right">{t('creditNotes.drafts.decision')}</th>}
          </tr>
        </thead>
        <tbody>
          {drafts.map((d) => (
            <tr key={d.id} className="border-t border-warning/20">
              <td className="py-1 font-mono">{d.orderNumber ?? d.orderId}</td>
              <td>{d.customerName ?? '—'}</td>
              <td className="text-right font-medium">{formatEUR(d.amountTTC)}</td>
              <td>{formatDate(d.createdAt)}</td>
              {canDecide && (
                <td className="text-right whitespace-nowrap">
                  <Button size="sm" disabled={busy === d.id} onClick={() => issue(d)}>
                    <FileCheck className="h-4 w-4" />
                    {t('creditNotes.drafts.issue')}
                  </Button>{' '}
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy === d.id}
                    onClick={() => dismiss(d)}
                  >
                    <X className="h-4 w-4" />
                    {t('creditNotes.drafts.dismiss')}
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

interface LineDraft {
  productName: string;
  productSku: string;
  quantity: string;
  unitPriceHT: string;
  vatRate: string;
}

const emptyLine = (): LineDraft => ({
  productName: '',
  productSku: '',
  quantity: '1',
  unitPriceHT: '0',
  vatRate: '20',
});

/** Manual credit-note creation form (POST /credit-notes). */
function CreateCreditNoteModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoiceId, setInvoiceId] = useState('');
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient
      .get<Invoice[]>('/invoices')
      .then((r) => setInvoices(r.data ?? []))
      .catch(showApiError);
  }, [showApiError]);

  const totals = useMemo(() => {
    let ht = 0;
    let vat = 0;
    for (const l of lines) {
      const lineHt = (Number(l.quantity) || 0) * (Number(l.unitPriceHT) || 0);
      ht += lineHt;
      vat += lineHt * ((Number(l.vatRate) || 0) / 100);
    }
    return { ht, vat, ttc: ht + vat };
  }, [lines]);

  const setLine = (i: number, patch: Partial<LineDraft>) =>
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const submit = async () => {
    if (!invoiceId)
      return push({ tone: 'warning', message: t('creditNotes.form.selectInvoiceFirst') });
    if (reason.trim().length < 3)
      return push({ tone: 'warning', message: t('creditNotes.form.reasonTooShort') });
    const payloadLines = lines
      .filter((l) => l.productName.trim())
      .map((l) => ({
        productName: l.productName.trim(),
        productSku: l.productSku.trim() || null,
        quantity: Number(l.quantity),
        unitPriceHT: Number(l.unitPriceHT),
        vatRate: l.vatRate === '' ? undefined : Number(l.vatRate),
      }));
    if (payloadLines.length === 0)
      return push({ tone: 'warning', message: t('creditNotes.form.addOneLine') });
    setSaving(true);
    try {
      const { data } = await apiClient.post<{ creditNoteNumber: string }>(
        '/credit-notes',
        { invoiceId, reason: reason.trim(), lines: payloadLines },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: 'success',
        message: t('creditNotes.form.created', { number: data.creditNoteNumber }),
      });
      onCreated();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <aside className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border bg-background shadow-xl">
        <header className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-lg font-semibold">{t('creditNotes.form.heading')}</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1"
            aria-label={t('creditNotes.detail.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="cn-invoice">{t('creditNotes.form.originalInvoice')}</Label>
              <Select
                id="cn-invoice"
                value={invoiceId}
                onChange={(e) => setInvoiceId(e.target.value)}
              >
                <option value="">{t('creditNotes.form.selectInvoice')}</option>
                {invoices.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoice_number}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="cn-reason">{t('creditNotes.form.reason')}</Label>
              <Input
                id="cn-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('creditNotes.form.reasonPlaceholder')}
              />
            </div>
          </div>

          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold">{t('creditNotes.form.lines')}</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLines((ls) => [...ls, emptyLine()])}
              >
                <Plus className="h-4 w-4" /> {t('creditNotes.form.addLine')}
              </Button>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 items-end gap-2 rounded border bg-card p-2"
                >
                  <div className="col-span-4">
                    {i === 0 && (
                      <Label className="text-xs">{t('creditNotes.form.productName')}</Label>
                    )}
                    <Input
                      value={l.productName}
                      onChange={(e) => setLine(i, { productName: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <Label className="text-xs">{t('creditNotes.form.sku')}</Label>}
                    <Input
                      value={l.productSku}
                      onChange={(e) => setLine(i, { productSku: e.target.value })}
                    />
                  </div>
                  <div className="col-span-1">
                    {i === 0 && <Label className="text-xs">{t('common.qty')}</Label>}
                    <Input
                      type="number"
                      min="1"
                      value={l.quantity}
                      onChange={(e) => setLine(i, { quantity: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <Label className="text-xs">{t('creditNotes.form.unitHt')}</Label>}
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={l.unitPriceHT}
                      onChange={(e) => setLine(i, { unitPriceHT: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <Label className="text-xs">{t('creditNotes.form.vatPct')}</Label>}
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      value={l.vatRate}
                      onChange={(e) => setLine(i, { vatRate: e.target.value })}
                    />
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <button
                      onClick={() =>
                        setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls))
                      }
                      className="text-muted-foreground hover:text-destructive p-1.5"
                      aria-label={t('creditNotes.form.removeLine')}
                      disabled={lines.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <dl className="ml-auto w-full max-w-xs space-y-0.5 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t('creditNotes.form.subtotalHt')}</dt>
              <dd>{formatEUR(totals.ht)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t('common.vat')}</dt>
              <dd>{formatEUR(totals.vat)}</dd>
            </div>
            <div className="flex justify-between font-semibold">
              <dt>{t('creditNotes.form.totalTtc')}</dt>
              <dd>{formatEUR(totals.ttc)}</dd>
            </div>
          </dl>
        </div>

        <footer className="flex justify-end gap-2 border-t px-4 py-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            <X className="h-4 w-4" />
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={saving}>
            <FilePlus className="h-4 w-4" />
            {saving ? t('creditNotes.form.creating') : t('creditNotes.form.create')}
          </Button>
        </footer>
      </aside>
    </div>
  );
}

/** Right-side panel showing the avoir summary + an inline PDF preview. */
function CreditNoteDetailPanel({
  creditNote,
  onClose,
}: {
  creditNote: CreditNote;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    let url: string | null = null;
    let alive = true;
    (async () => {
      try {
        const res = await apiClient.get(`/credit-notes/${creditNote.id}/pdf`, {
          responseType: 'blob',
        });
        if (!alive) return;
        url = URL.createObjectURL(res.data as Blob);
        setPdfUrl(url);
      } catch (err) {
        showApiError(err);
      }
    })();
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [creditNote.id, showApiError]);

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex justify-between gap-4 py-0.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <aside className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border bg-background shadow-xl">
        <header className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="truncate text-lg font-semibold" title={creditNote.credit_note_number}>
            {t('creditNotes.detail.heading', { number: creditNote.credit_note_number })}
          </h2>
          <div className="flex items-center gap-2">
            {pdfUrl && (
              <a
                href={pdfUrl}
                download={`avoir-${creditNote.credit_note_number}.pdf`}
                className="inline-flex items-center gap-1 rounded-md border border-input px-2.5 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
              >
                <Download className="h-4 w-4" /> PDF
              </a>
            )}
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground p-1"
              aria-label={t('creditNotes.detail.close')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
          <section className="rounded border bg-card p-4">
            <dl className="text-sm">
              <Row label={t('creditNotes.detail.number')}>
                <span className="font-mono">{creditNote.credit_note_number}</span>
              </Row>
              <Row label={t('common.status')}>{enumLabel('docStatus', creditNote.status)}</Row>
              <Row label={t('creditNotes.detail.reason')}>{creditNote.reason}</Row>
              <Row label={t('creditNotes.detail.created')}>{formatDate(creditNote.created_at)}</Row>
              <Row label={t('creditNotes.detail.subtotalHt')}>
                {formatEUR(creditNote.subtotal_ht)}
              </Row>
              <Row label={t('common.vat')}>{formatEUR(creditNote.vat_amount)}</Row>
              <Row label={t('creditNotes.detail.totalTtc')}>
                <span className="font-semibold">{formatEUR(creditNote.total_ttc)}</span>
              </Row>
            </dl>
          </section>

          <section className="flex min-h-[24rem] flex-1 flex-col rounded border bg-card">
            <h3 className="border-b px-3 py-2 text-sm font-semibold">
              {t('creditNotes.detail.document')}
            </h3>
            {pdfUrl ? (
              <iframe
                title={t('creditNotes.detail.heading', {
                  number: creditNote.credit_note_number,
                })}
                src={pdfUrl}
                className="h-full w-full flex-1 rounded-b"
              />
            ) : (
              <div className="p-4">
                <Skeleton className="h-80 w-full" />
              </div>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}
